import { useCallback, useEffect, useState, type RefObject } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { getCheckoutDrawerHostBottomInset, readCheckoutDrawerBottomInset } from '@/utils/checkoutDrawerBottomInset';

const INTERSECTION_THRESHOLDS = Array.from({ length: 101 }, (_, index) => index / 100);

/**
 * Bottom inset (px) for {@link CheckoutBottomSheet} only — the PREFERRED
 * checkout swipe-up drawer. Not for `Common/Drawer` / `ScreenDrawer` / Modal.
 * See `checkoutDrawerBottomInset.ts` for the iframe + Chrome/Safari root cause.
 * Combines the iframe's visualViewport with the root element's cross-origin
 * intersection rectangle so parent clipping is measured without parent access.
 */
export const useCheckoutDrawerBottomInset = (rootRef: RefObject<HTMLElement>): number => {
  const isMobile = useIsMobile();
  const read = useCallback(() => readCheckoutDrawerBottomInset(), []);
  const [viewportInset, setViewportInset] = useState(read);
  const [hostInset, setHostInset] = useState(0);

  useEffect(() => {
    const update = () => setViewportInset(read());
    update();

    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [read]);

  useEffect(() => {
    const target = rootRef.current;
    if (!isMobile || !target || typeof IntersectionObserver !== 'function') {
      setHostInset(0);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        setHostInset(
          getCheckoutDrawerHostBottomInset({
            isIntersecting: entry.isIntersecting,
            targetBottom: entry.boundingClientRect.bottom,
            intersectionBottom: entry.intersectionRect.bottom,
          })
        );
      },
      { threshold: INTERSECTION_THRESHOLDS }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isMobile, rootRef]);

  return Math.max(viewportInset, hostInset);
};
